// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { getSSRForcedRelay } from '@confluence/apollo-relay-interop/entry-points/ssrForcedRelay.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { isUnauthorizedError } from '@confluence/unauthorized-error-boundary';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';

import type { SessionDataType } from './SessionDataTypes.ts';
import SiteInformationQuery, {
	type SiteInformationQuery$data,
	type SiteInformationQuery$variables,
} from './__generated__/SiteInformationQuery.graphql.ts';

declare global {
	interface Window {
		__SITEINFORMATION_PROMISE__: Promise<SessionDataType> | null;
		__SITEINFORMATION__:
			| Promise<SiteInformationQuery$data | null>
			| SiteInformationQuery$data
			| Promise<null>
			| null;
	}
}

let sessionDataError: Error | undefined;

/** Returns the error captured by the singleton session-data request, once that request has settled. */
export const getSessionDataError = (): Error | undefined => sessionDataError;

const normalizeSessionDataError = (error: unknown): Error => {
	const errorCandidate = Array.isArray(error)
		? (error.find((candidate) => candidate instanceof Error) ?? error[0])
		: error;

	if (errorCandidate instanceof Error) {
		return errorCandidate;
	}

	const message =
		typeof errorCandidate === 'object' &&
		errorCandidate !== null &&
		'message' in errorCandidate &&
		typeof errorCandidate.message === 'string'
			? errorCandidate.message
			: 'Unknown session data error';

	return new Error(message);
};

export async function loadSessionData(): Promise<SiteInformationQuery$data | null> {
	// __SITEINFORMATION__ is either actual session data
	// or the promise that is resolving the session data
	// In the case when page is rendered by SSR, the __SITEINFORMATION__ will be populated with JSON data.
	if (window['__SITEINFORMATION__']) {
		return window['__SITEINFORMATION__'];
	}

	sessionDataError = undefined;

	window['__SITEINFORMATION__'] = queryInterop<
		SiteInformationQuery$data,
		SiteInformationQuery$variables
	>({
		query: SiteInformationQuery,
		errorPolicy: 'all',
		fetchPolicy: 'cache-first',
		// On the Bifrost SSR path this call runs before the Statsig client is initialised, so
		// `isRelayEnabled` cannot read the rollout configuration and silently answers "Apollo" —
		// while `SPAViewContextProvider` reads the same query after initialisation and resolves to
		// Relay, finding nothing. Bifrost resolves the routing up front from the native edge SDK;
		// `null` (every other environment) leaves the normal decision untouched.
		forceRelay: getSSRForcedRelay(),
		context: {
			single: false,
			allowOnExternalPage: true,
		},
	})
		.then((result) => {
			sessionDataError = result.error;
			const errors = result.error?.graphQLErrors ?? [];
			if (errors.length > 0) {
				errors.forEach((e) => {
					if (isUnauthorizedError(e) && !!result?.data?.tenant?.cloudId) {
						markErrorAsHandled(e);
					} else {
						getMonitoringClient().submitError(e, {
							isSiteInformationError: true,
							partialFailure: true,
							attribution: 'FATAL',
						});
					}
				});
			}
			return result.data;
		})
		.catch((error) => {
			sessionDataError = sessionDataError ?? normalizeSessionDataError(error);

			if (Array.isArray(error)) {
				error.forEach((e) =>
					getMonitoringClient().submitError(e, {
						isSiteInformationError: true,
						partialFailure: false,
						attribution: 'FATAL',
					}),
				);
			} else {
				getMonitoringClient().submitError(error, {
					isSiteInformationError: true,
					partialFailure: false,
					attribution: 'FATAL',
				});
			}

			return null;
		});

	return window['__SITEINFORMATION__'];
}
